import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import MobileNav from "../components/layout/MobileNav";
import { 
  User,
  Mail,
  Phone,
  MapPin,
  Home,
  ArrowLeft,
  Save,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Plus,
  Search
} from "lucide-react";

const OwnerProfilePage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [claims, setClaims] = useState([]);
  const [availableProperties, setAvailableProperties] = useState([]);
  
  // Edit state
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    phone_primary: "",
    phone_secondary: "",
    mailing_address: "",
    email_secondary: "",
    preferred_contact_method: "email"
  });
  
  // Claim property dialog
  const [claimOpen, setClaimOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/owners/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.has_profile === false) {
        setProfile(null);
      } else {
        setProfile(response.data);
        setFormData({
          phone_primary: response.data.phone_primary || "",
          phone_secondary: response.data.phone_secondary || "",
          mailing_address: response.data.mailing_address || "",
          email_secondary: response.data.email_secondary || "",
          preferred_contact_method: response.data.preferred_contact_method || "email"
        });
      }
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      toast.error("Failed to load profile");
    }
  };

  const fetchClaims = async () => {
    try {
      const response = await axios.get(`${API}/owners/property-claims`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaims(response.data);
    } catch (error) {
      console.error("Failed to fetch claims:", error);
    }
  };

  const fetchAvailableProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out properties already linked to this owner
      const myPropertyIds = profile?.property_ids || [];
      const available = response.data.filter(p => !myPropertyIds.includes(p.property_id));
      setAvailableProperties(available);
    } catch (error) {
      console.error("Failed to fetch properties:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchProfile();
      await fetchClaims();
      setLoading(false);
    };
    loadData();
  }, [token]);

  useEffect(() => {
    if (claimOpen) {
      fetchAvailableProperties();
    }
  }, [claimOpen, profile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/owners/me`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated successfully");
      setEditMode(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleClaimProperty = async () => {
    if (!selectedProperty) {
      toast.error("Please select a property");
      return;
    }
    
    try {
      await axios.post(`${API}/owners/me/claim-property`, null, {
        params: { property_id: selectedProperty },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Property claim submitted for board review");
      setClaimOpen(false);
      setSelectedProperty("");
      fetchClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit claim");
    }
  };

  const filteredProperties = availableProperties.filter(p => 
    !searchQuery || 
    p.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.lot_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getClaimStatusBadge = (status) => {
    const styles = {
      pending: { className: "bg-yellow-500/10 text-yellow-600", icon: <Clock className="w-3 h-3 mr-1" /> },
      approved: { className: "bg-green-500/10 text-green-600", icon: <CheckCircle className="w-3 h-3 mr-1" /> },
      denied: { className: "bg-red-500/10 text-red-600", icon: <XCircle className="w-3 h-3 mr-1" /> }
    };
    const style = styles[status] || styles.pending;
    return (
      <Badge className={`${style.className} flex items-center`}>
        {style.icon}
        {status?.toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-20 md:pb-0">
        <p className="text-muted-foreground">Loading profile...</p>
        <MobileNav />
      </div>
    );
  }

  // No profile linked - show setup message
  if (!profile) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <header className="border-b bg-card sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 md:px-8 py-3 md:py-4 flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/owner")} className="text-muted-foreground">
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              <span className="font-serif text-base md:text-lg font-semibold">My Profile</span>
            </div>
          </div>
        </header>
        
        <main className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-8">
          <Alert className="border-yellow-200 bg-yellow-50">
            <AlertTriangle className="w-4 h-4 text-yellow-600" />
            <AlertDescription>
              <p className="font-semibold text-yellow-800">No Owner Profile Found</p>
              <p className="text-yellow-700 mt-1 text-sm md:text-base">
                Your account is not yet linked to an owner profile. Please contact the HOA board at{" "}
                <a href="mailto:board@horsemountainranch.com" className="underline">board@horsemountainranch.com</a>{" "}
                to have your profile created and linked to your property.
              </p>
            </AlertDescription>
          </Alert>
          
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg md:text-xl">Your Account Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm md:text-base truncate">{user?.email}</span>
              </div>
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm md:text-base">{user?.name || "No name set"}</span>
              </div>
            </CardContent>
          </Card>
        </main>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/owner")} className="text-muted-foreground px-2 md:px-3">
              <ArrowLeft className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Back</span>
            </Button>
            <div className="flex items-center gap-2 md:gap-3">
              <User className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              <span className="font-serif text-base md:text-lg font-semibold">My Profile</span>
            </div>
          </div>
          {!editMode ? (
            <Button variant="outline" size="sm" onClick={() => setEditMode(true)} data-testid="edit-profile-btn" className="text-xs md:text-sm">
              Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditMode(false)} className="text-xs md:text-sm">Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-profile-btn" className="text-xs md:text-sm">
                <Save className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                {saving ? "..." : "Save"}
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-4 md:py-8">
        <Tabs defaultValue="profile" className="space-y-4 md:space-y-6">
          <TabsList className="w-full md:w-auto grid grid-cols-2 md:inline-flex">
            <TabsTrigger value="profile" className="text-xs md:text-sm">Profile</TabsTrigger>
            <TabsTrigger value="properties" className="text-xs md:text-sm">Properties</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-4 md:space-y-6">
            {/* Basic Info Card */}
            <Card>
              <CardHeader className="pb-3 md:pb-6">
                <CardTitle className="text-base md:text-lg">Personal Information</CardTitle>
                <CardDescription className="text-xs md:text-sm">Your account and contact details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1 md:space-y-2">
                    <Label className="text-muted-foreground text-xs md:text-sm">Full Name</Label>
                    <p className="font-medium text-sm md:text-base">{profile.first_name} {profile.last_name}</p>
                  </div>
                  <div className="space-y-1 md:space-y-2">
                    <Label className="text-muted-foreground text-xs md:text-sm">Primary Email</Label>
                    <p className="font-medium text-sm md:text-base truncate">{profile.email_primary}</p>
                  </div>
                  <div className="space-y-1 md:space-y-2">
                    <Label className="text-muted-foreground text-xs md:text-sm">Secondary Email</Label>
                    {editMode ? (
                      <Input
                        type="email"
                        value={formData.email_secondary}
                        onChange={(e) => setFormData({...formData, email_secondary: e.target.value})}
                        placeholder="Optional secondary email"
                      />
                    ) : (
                      <p className="font-medium">{profile.email_secondary || "Not set"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm">Owner Status</Label>
                    <Badge className={profile.status === "active" ? "bg-green-500/10 text-green-600" : "bg-gray-500/10 text-gray-600"}>
                      {profile.status?.toUpperCase()}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contact Information</CardTitle>
                <CardDescription>How the HOA can reach you</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Primary Phone
                    </Label>
                    {editMode ? (
                      <Input
                        type="tel"
                        value={formData.phone_primary}
                        onChange={(e) => setFormData({...formData, phone_primary: e.target.value})}
                        placeholder="(555) 123-4567"
                        data-testid="phone-primary-input"
                      />
                    ) : (
                      <p className="font-medium">{profile.phone_primary || "Not set"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Secondary Phone
                    </Label>
                    {editMode ? (
                      <Input
                        type="tel"
                        value={formData.phone_secondary}
                        onChange={(e) => setFormData({...formData, phone_secondary: e.target.value})}
                        placeholder="(555) 123-4567"
                      />
                    ) : (
                      <p className="font-medium">{profile.phone_secondary || "Not set"}</p>
                    )}
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-muted-foreground text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Mailing Address
                    </Label>
                    {editMode ? (
                      <Input
                        value={formData.mailing_address}
                        onChange={(e) => setFormData({...formData, mailing_address: e.target.value})}
                        placeholder="123 Main St, City, State ZIP"
                        data-testid="mailing-address-input"
                      />
                    ) : (
                      <p className="font-medium">{profile.mailing_address || "Not set"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm">Preferred Contact Method</Label>
                    {editMode ? (
                      <Select 
                        value={formData.preferred_contact_method} 
                        onValueChange={(v) => setFormData({...formData, preferred_contact_method: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="phone">Phone</SelectItem>
                          <SelectItem value="mail">Mail</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium capitalize">{profile.preferred_contact_method || "Email"}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Balance Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Account Balance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Current Dues Balance</p>
                    <p className={`text-2xl font-bold ${profile.dues_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      ${(profile.dues_balance || 0).toFixed(2)}
                    </p>
                  </div>
                  {profile.dues_balance > 0 && (
                    <Button onClick={() => navigate("/owner/payments")} data-testid="make-payment-btn">
                      Make Payment
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="properties" className="space-y-6">
            {/* Linked Properties */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">My Properties</CardTitle>
                  <CardDescription>Properties linked to your account</CardDescription>
                </div>
                <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="claim-property-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Claim Property
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Claim a Property</DialogTitle>
                      <DialogDescription>
                        Select a property to link to your account. Your claim will be reviewed by the board.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by address or lot number..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                      <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                        <SelectTrigger data-testid="select-property">
                          <SelectValue placeholder="Select a property" />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredProperties.map(p => (
                            <SelectItem key={p.property_id} value={p.property_id}>
                              Lot {p.lot_number} - {p.address}
                            </SelectItem>
                          ))}
                          {filteredProperties.length === 0 && (
                            <div className="p-2 text-sm text-muted-foreground text-center">
                              No properties available
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setClaimOpen(false)}>Cancel</Button>
                      <Button onClick={handleClaimProperty} disabled={!selectedProperty}>
                        Submit Claim
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {profile.properties?.length > 0 ? (
                  <div className="space-y-3">
                    {profile.properties.map((property) => (
                      <div key={property.property_id} className="p-4 border rounded-lg">
                        <div className="flex items-start gap-4">
                          <Building2 className="w-5 h-5 text-primary mt-1" />
                          <div className="flex-1">
                            <p className="font-semibold">Lot {property.lot_number}</p>
                            <p className="text-sm text-muted-foreground">{property.address}</p>
                            <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                              {property.acreage && <span>{property.acreage} acres</span>}
                              {property.property_type && <span>• {property.property_type}</span>}
                            </div>
                          </div>
                          <Badge variant="outline" className="bg-green-50 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Linked
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No properties linked to your account</p>
                    <p className="text-sm mt-1">Click "Claim Property" to link your property</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Claims */}
            {claims.length > 0 && (
              <Card>
                <CardHeader className="pb-3 md:pb-6">
                  <CardTitle className="text-base md:text-lg">Property Claims</CardTitle>
                  <CardDescription className="text-xs md:text-sm">Status of your property claim requests</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {claims.map((claim) => (
                      <div key={claim.claim_id} className="p-3 md:p-4 border rounded-lg">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-sm md:text-base">Lot {claim.lot_number}</p>
                            <p className="text-xs md:text-sm text-muted-foreground truncate">{claim.property_address}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Submitted: {new Date(claim.created_at).toLocaleDateString()}
                            </p>
                            {claim.denial_reason && (
                              <p className="text-xs md:text-sm text-red-600 mt-2">Reason: {claim.denial_reason}</p>
                            )}
                          </div>
                          {getClaimStatusBadge(claim.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Mobile Navigation */}
      <MobileNav />
    </div>
  );
};

export default OwnerProfilePage;
